import { ROLE_LABEL } from "../../lib/roleOptions";

// EXPENSES ONLY. Leaves and tour plans have their own badge
// (`components/leaves/StatusBadge.jsx`) — which is why "Approved for payment
// release" can live here without leaking onto a leave, where it would be
// nonsense.
//
// **"Approved for payment release" IS A LABEL, NOT A STATE.** It renames what
// the final approval already means: the claim has cleared its chain and is due
// to be paid. There is deliberately no payment-status field behind it, no
// release action, and no record of whether money actually moved — adding any
// of those would be a new workflow wearing a wording change, and this is not
// that. The stored status is still exactly `Approved`.
const MAP = {
  Draft: { label: "Draft", bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300", dot: "bg-slate-400" },
  Submitted: { label: "Submitted", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  Approved: { label: "Approved for payment release", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  Rejected: { label: "Rejected", bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500" },
};

// Nobody can act: the released-officer case, where stage 1 resolves to no
// Sales Manager at all. Red rather than amber, because this is not ordinary
// progress — the claim is stuck and will stay stuck until somebody is assigned.
const STUCK = {
  label: "Needs an approver",
  bg: "bg-red-50", text: "text-red-700", border: "border-red-200", dot: "bg-red-500",
};

/**
 * The in-flight label: WHERE the claim is, not what was done to it.
 *
 * "Submitted" describes an action, and it stops being true the moment stage 1
 * clears — the claim is then sitting with a specific person, and a badge still
 * reading "Submitted" contradicts the status line directly beneath it.
 *
 * **THE ROLE COMES FROM THE STAMPED ROUTE, NOT FROM A MAPPING HERE.**
 * `waiting_on.approver_role` is computed by `team_scope.waiting_on`, which
 * takes it from `approver_role_at` — the approval routing's own function — and
 * confirms it against `can_approve_submission`. This file does not know what
 * stage 1 of anything means and must never learn: a stage-to-role table here
 * would be a second source of truth for the one rule this codebase has watched
 * drift three times, and #78 shipped when two such lists disagreed.
 *
 * That is also what makes it right for the cases a hardcoded table gets wrong:
 *
 * - An ordinary Sales Officer claim ends at the **Admin Manager**. The Director
 *   is not its final stage and must not be named on it.
 * - She appears only on a manager-filed claim, or on an officer's claim
 *   escalated over the Settings threshold — where the route is stamped on the
 *   row and says so.
 *
 * It is also viewer-independent by construction: `waiting_on` is derived from
 * the claim and the org, never from who is looking, so the same card reads the
 * same way for the officer, both managers and the Director.
 */
export default function StatusBadge({ status, waitingOn = null, size = "md" }) {
  const base = MAP[status] || MAP.Draft;
  let v = base;

  if (status === "Submitted" && waitingOn) {
    const approvers = waitingOn.approvers || [];
    if (approvers.length === 0) {
      // Constraint: say so visibly rather than fall back to a generic label.
      // "Submitted" here would be the silent fallback that hides a stuck claim.
      v = STUCK;
    } else if (waitingOn.approver_role) {
      // Role KEY to display NAME. Presentation, not routing — the key itself
      // was decided by the server.
      const role = ROLE_LABEL[waitingOn.approver_role];
      if (role) v = { ...base, label: `With ${role}` };
    }
  }

  const sz = size === "sm" ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5";
  return (
    <span
      className={`inline-flex items-center gap-1 ${sz} font-semibold rounded-full border ${v.bg} ${v.text} ${v.border} whitespace-nowrap`}
      // Keyed on the STORED status, not the label. The badge now says where the
      // claim sits, but `Submitted` is still what the database holds and what
      // every filter and endpoint means — a testid that drifted to the display
      // text would stop matching the thing it identifies.
      data-testid={`expense-status-${status.toLowerCase()}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${v.dot}`} />
      {v.label}
    </span>
  );
}
